import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { X } from 'lucide-react';
import { InventoryItem, InventoryCategory } from '../../types/inventory';
import { inventoryAPI } from '../../services/inventoryAPI';

interface InventoryItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  item: InventoryItem | null;
  categories: InventoryCategory[];
}

const InventoryItemModal: React.FC<InventoryItemModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  item,
  categories,
}) => {
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';
  
  const [formData, setFormData] = useState({
    sku: '',
    name: '',
    name_ar: '',
    description: '',
    category_id: '',
    quantity_in_stock: 0,
    minimum_stock_level: 0,
    reorder_point: 0,
    cost_price: 0,
    selling_price: 0,
    unit_of_measure: 'unit',
    supplier_name: '',
    supplier_contact: '',
    location_in_store: '',
  });

  const [errors, setErrors] = useState<any>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (item) {
      setFormData({
        sku: item.sku,
        name: item.name,
        name_ar: item.name_ar || '',
        description: item.description || '',
        category_id: item.category_id?.toString() || '',
        quantity_in_stock: item.quantity_in_stock,
        minimum_stock_level: item.minimum_stock_level,
        reorder_point: item.reorder_point,
        cost_price: item.cost_price,
        selling_price: item.selling_price,
        unit_of_measure: item.unit_of_measure,
        supplier_name: item.supplier_name || '',
        supplier_contact: item.supplier_contact || '',
        location_in_store: item.location_in_store || '',
      });
    }
  }, [item]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: ['quantity_in_stock', 'minimum_stock_level', 'reorder_point', 'cost_price', 'selling_price'].includes(name)
        ? parseFloat(value) || 0
        : value,
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev: any) => ({ ...prev, [name]: undefined }));
    }
  };

  const validateForm = () => {
    const newErrors: any = {};

    if (!formData.sku.trim()) newErrors.sku = t('validation.required');
    if (!formData.name.trim()) newErrors.name = t('validation.required');
    if (formData.cost_price < 0) newErrors.cost_price = t('validation.positiveNumber');
    if (formData.selling_price < 0) newErrors.selling_price = t('validation.positiveNumber');
    if (formData.minimum_stock_level < 0) newErrors.minimum_stock_level = t('validation.positiveNumber');
    if (formData.reorder_point < 0) newErrors.reorder_point = t('validation.positiveNumber');

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    try {
      const data = {
        ...formData,
        category_id: formData.category_id ? parseInt(formData.category_id) : undefined,
      };

      if (item) {
        await inventoryAPI.updateItem(item.id, data);
      } else {
        await inventoryAPI.createItem(data);
      }

      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error saving item:', error);
      setErrors({ general: error.response?.data?.message || t('errors.generic') });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold text-gray-900">
            {item ? t('inventory.editItem') : t('inventory.addItem')}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {errors.general && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-600 rounded-md">
            {errors.general}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* SKU */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('inventory.fields.sku')} *
              </label>
              <input
                type="text"
                name="sku"
                value={formData.sku}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                  errors.sku ? 'border-red-300' : 'border-gray-300'
                }`}
                dir={isRTL ? 'rtl' : 'ltr'}
              />
              {errors.sku && (
                <p className="mt-1 text-sm text-red-600">{errors.sku}</p>
              )}
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('inventory.fields.category')}
              </label>
              <select
                name="category_id"
                value={formData.category_id}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">{t('inventory.selectCategory')}</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {isRTL ? category.name_ar || category.name : category.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Name */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('inventory.fields.name')} *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                  errors.name ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('inventory.fields.nameAr')}
              </label>
              <input
                type="text"
                name="name_ar"
                value={formData.name_ar}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                dir="rtl"
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('inventory.fields.description')}
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              dir={isRTL ? 'rtl' : 'ltr'}
            />
          </div>

          {/* Stock Levels */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('inventory.fields.currentStock')}
              </label>
              <input
                type="number"
                name="quantity_in_stock"
                value={formData.quantity_in_stock}
                onChange={handleChange}
                min="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('inventory.fields.minimumStock')}
              </label>
              <input
                type="number"
                name="minimum_stock_level"
                value={formData.minimum_stock_level}
                onChange={handleChange}
                min="0"
                className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                  errors.minimum_stock_level ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {errors.minimum_stock_level && (
                <p className="mt-1 text-sm text-red-600">{errors.minimum_stock_level}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('inventory.fields.reorderPoint')}
              </label>
              <input
                type="number"
                name="reorder_point"
                value={formData.reorder_point}
                onChange={handleChange}
                min="0"
                className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                  errors.reorder_point ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {errors.reorder_point && (
                <p className="mt-1 text-sm text-red-600">{errors.reorder_point}</p>
              )}
            </div>
          </div>

          {/* Pricing */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('inventory.fields.costPrice')}
              </label>
              <input
                type="number"
                name="cost_price"
                value={formData.cost_price}
                onChange={handleChange}
                min="0"
                step="0.01"
                className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                  errors.cost_price ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {errors.cost_price && (
                <p className="mt-1 text-sm text-red-600">{errors.cost_price}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('inventory.fields.sellingPrice')}
              </label>
              <input
                type="number"
                name="selling_price"
                value={formData.selling_price}
                onChange={handleChange}
                min="0"
                step="0.01"
                className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                  errors.selling_price ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {errors.selling_price && (
                <p className="mt-1 text-sm text-red-600">{errors.selling_price}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('inventory.fields.unit')}
              </label>
              <input
                type="text"
                name="unit_of_measure"
                value={formData.unit_of_measure}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Supplier Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('inventory.fields.supplier')}
              </label>
              <input
                type="text"
                name="supplier_name"
                value={formData.supplier_name}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('inventory.fields.supplierContact')}
              </label>
              <input
                type="text"
                name="supplier_contact"
                value={formData.supplier_contact}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Location */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('inventory.fields.location')}
            </label>
            <input
              type="text"
              name="location_in_store"
              value={formData.location_in_store}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {loading ? t('common.saving') : t('common.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InventoryItemModal;